import {
  setupVisibilityCommands,
  type VisibilityHookContext
} from '../helpers/visibility'

type PlacementSide = 'top' | 'right' | 'bottom' | 'left'
type PlacementAlignment = 'start' | 'end'
type Placement = PlacementSide | `${PlacementSide}-${PlacementAlignment}`

type ParsedPlacement = {
  readonly side: PlacementSide
  readonly alignment?: PlacementAlignment
}

type PopoverActions = {
  readonly show: () => void | Promise<void>
  readonly hide: () => void | Promise<void>
}

type PopoverState = {
  readonly open?: boolean
}

type PopupPlacementApi = {
  flipFallbackPlacements?: string
  flipFallbackStrategy?: 'best-fit' | 'initial'
  readonly reposition?: () => void
}

type PopoverController = Readonly<{
  cleanup: () => void
  update: () => void
}>

type PopoverHookContext = VisibilityHookContext & {
  popoverController?: PopoverController
}

const DEFAULT_PLACEMENT: Placement = 'bottom-start'
const FOCUSABLE_TRIGGER_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

const OPPOSITE_SIDE: Readonly<Record<PlacementSide, PlacementSide>> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right'
}

const isPlacementSide = (value: string): value is PlacementSide =>
  value === 'top' || value === 'right' || value === 'bottom' || value === 'left'

const isPlacementAlignment = (
  value: string | undefined
): value is PlacementAlignment => value === 'start' || value === 'end'

const parsePlacement = (placement: string): ParsedPlacement | undefined => {
  const [side, alignment] = placement.split('-', 2)

  if (!isPlacementSide(side)) {
    return undefined
  }

  if (alignment === undefined) {
    return { side }
  }

  return isPlacementAlignment(alignment) ? { side, alignment } : undefined
}

const formatPlacement = ({ side, alignment }: ParsedPlacement): Placement =>
  alignment === undefined ? side : `${side}-${alignment}`

export const oppositeSameAlignmentPlacement = (
  placement: string
): Placement => {
  const parsedPlacement = parsePlacement(placement) ?? {
    side: 'bottom',
    alignment: 'start'
  }

  return formatPlacement({
    side: OPPOSITE_SIDE[parsedPlacement.side],
    alignment: parsedPlacement.alignment
  })
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const hasPopoverActions = (value: unknown): value is PopoverActions =>
  isRecord(value) &&
  typeof value.show === 'function' &&
  typeof value.hide === 'function'

const hasPopoverState = (value: unknown): value is PopoverState =>
  isRecord(value) &&
  (value.open === undefined || typeof value.open === 'boolean')

const getPopoverPlacement = (element: HTMLElement): string =>
  element.getAttribute('placement') ?? DEFAULT_PLACEMENT

const resolvePopoverAnchor = (element: HTMLElement): HTMLElement | null => {
  const triggerId = element.dataset.for

  if (!element.isConnected || triggerId === undefined) {
    return null
  }

  const trigger = document.getElementById(triggerId)

  if (!(trigger instanceof HTMLElement)) {
    return null
  }

  const focusableTrigger = trigger.querySelector(FOCUSABLE_TRIGGER_SELECTOR)
  const anchor =
    focusableTrigger instanceof HTMLElement ? focusableTrigger : trigger

  if (!anchor.id) {
    anchor.id = `${element.id}-anchor`
  }

  return anchor
}

export const connectPopoverAnchor = (
  element: HTMLElement
): HTMLElement | null => {
  const anchor = resolvePopoverAnchor(element)

  if (anchor && element.getAttribute('for') !== anchor.id) {
    element.setAttribute('for', anchor.id)
  }

  return anchor
}

const getPopup = (element: HTMLElement): unknown =>
  isRecord(element) ? element.popup : undefined

const hasPopupPlacementApi = (value: unknown): value is PopupPlacementApi =>
  value instanceof HTMLElement || isRecord(value)

const configurePopupFallback = (
  popup: PopupPlacementApi,
  fallbackPlacement: Placement
): void => {
  if (popup instanceof HTMLElement) {
    popup.setAttribute('flip-fallback-placements', fallbackPlacement)
    popup.setAttribute('flip-fallback-strategy', 'initial')
  }

  popup.flipFallbackPlacements = fallbackPlacement
  popup.flipFallbackStrategy = 'initial'
  popup.reposition?.()
}

export const shouldPreserveAlignment = (element: HTMLElement): boolean =>
  element.dataset.preserveAlignment === 'true' ||
  (element.hasAttribute('data-preserve-alignment') &&
    element.dataset.preserveAlignment !== 'false')

export const configurePopoverAlignment = (element: HTMLElement): void => {
  if (!shouldPreserveAlignment(element)) {
    return
  }

  const popup = getPopup(element)

  if (!hasPopupPlacementApi(popup)) {
    return
  }

  configurePopupFallback(
    popup,
    oppositeSameAlignmentPlacement(getPopoverPlacement(element))
  )
}

// Reads whether the popover is currently shown.
const isPopoverOpen = (element: HTMLElement): boolean =>
  (hasPopoverState(element) && element.open === true) ||
  element.hasAttribute('open')

// Moves focus back to the content's autofocus control, as opening does.
const focusAutofocusControl = (element: HTMLElement): void => {
  element
    .querySelector<HTMLElement>('[autofocus]')
    ?.focus({ preventScroll: true })
}

// Opens the popover, or refocuses its autofocus control when already open.
const showPopover = (element: HTMLElement): void => {
  if (!hasPopoverActions(element)) {
    return
  }

  if (isPopoverOpen(element)) {
    focusAutofocusControl(element)
    return
  }

  element.show()
}

const hidePopover = (element: HTMLElement): void => {
  if (hasPopoverActions(element)) {
    element.hide()
  }
}

const togglePopover = (element: HTMLElement): void => {
  if (isPopoverOpen(element)) {
    hidePopover(element)
    return
  }

  showPopover(element)
}

const bindFallbackAnchorKeyboard = (
  element: HTMLElement,
  anchor: HTMLElement | null
): (() => void) => {
  const trigger = document.getElementById(element.dataset.for ?? '')

  if (!(anchor && anchor === trigger)) {
    return () => {}
  }

  const originalRole = anchor.getAttribute('role')
  const originalTabIndex = anchor.getAttribute('tabindex')

  anchor.setAttribute('role', originalRole ?? 'button')
  anchor.tabIndex = Math.max(anchor.tabIndex, 0)
  anchor.dataset.facadePopoverFallback = 'true'

  const handleKeydown = (event: KeyboardEvent): void => {
    if (!(event.key === 'Enter' || event.key === ' ')) {
      return
    }

    event.preventDefault()
    togglePopover(element)
  }

  anchor.addEventListener('keydown', handleKeydown)

  return () => {
    anchor.removeEventListener('keydown', handleKeydown)
    delete anchor.dataset.facadePopoverFallback

    if (originalRole === null) {
      anchor.removeAttribute('role')
    } else {
      anchor.setAttribute('role', originalRole)
    }

    if (originalTabIndex === null) {
      anchor.removeAttribute('tabindex')
    } else {
      anchor.setAttribute('tabindex', originalTabIndex)
    }
  }
}

const setupPopover = (context: PopoverHookContext): PopoverController => {
  let disconnectAnchor = (): void => undefined
  const anchorObserver = new MutationObserver(() => connectAnchor())

  const configureAlignment = (): void => {
    connectPopoverAnchor(context.el)
    configurePopoverAlignment(context.el)
  }

  const connectAnchor = (): void => {
    disconnectAnchor()
    disconnectAnchor = bindFallbackAnchorKeyboard(
      context.el,
      connectPopoverAnchor(context.el)
    )
  }

  const observeAnchor = (): void => {
    anchorObserver.disconnect()

    const trigger = document.getElementById(context.el.dataset.for ?? '')

    if (trigger) {
      anchorObserver.observe(trigger, {
        attributes: true,
        attributeFilter: ['id'],
        childList: true,
        subtree: true
      })
    }
  }

  connectAnchor()
  observeAnchor()
  configurePopoverAlignment(context.el)

  context.el.addEventListener('wa-show', configureAlignment)
  context.el.addEventListener('wa-after-show', configureAlignment)

  const cleanupCommands = setupVisibilityCommands(context, 'popover', {
    hide: () => hidePopover(context.el),
    show: () => showPopover(context.el)
  })

  return {
    cleanup: () => {
      disconnectAnchor()
      anchorObserver.disconnect()
      cleanupCommands()
      context.el.removeEventListener('wa-show', configureAlignment)
      context.el.removeEventListener('wa-after-show', configureAlignment)
    },
    update: () => {
      connectAnchor()
      observeAnchor()
      configurePopoverAlignment(context.el)
    }
  }
}

export const FacadePopover = {
  mounted(this: PopoverHookContext) {
    this.popoverController?.cleanup()
    this.popoverController = setupPopover(this)
  },

  updated(this: PopoverHookContext) {
    this.popoverController?.update()
  },

  destroyed(this: PopoverHookContext) {
    this.popoverController?.cleanup()
    this.popoverController = undefined
  }
}
